import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
	clearAdvanceFilter,
	setAdvanceFilter,
	setAdvanceFilterPage,
	setAdvanceFilterTotalPage,
	setSelectedInput,
	setSelectedInputData,
	setSelectedInputValue,
} from '../../../../../store/action/advanceFilter';
import { formatFilterName, getFiltersParams, getUniqueFilterOptions } from '../../../../../helper';
import { toast } from 'react-toastify';
import {
	deleteSalesReturn,
	generateSalesReturnsPdf,
	getSalesReturnList,
} from '../../../../../services/sales_return';
import { getDate } from '../../../../../utils/dateUtils';
import { hasPermission } from '../../../../../utils/controller';

const UseSalesReturn = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [bulkAction, setBulkAction] = useState([]);
	const user = useSelector((state) => state?.userInfo?.userInfo?.data);
	const [deselectRows, setDeselectRows] = useState(null);
	const [isBulkDeleteModelOpen, setIsBulkDeleteModalOpen] = useState(false);
	const [deleteBulkReturnList, setDeleteBulkReturnList] = useState(null);
	const [headerDropDown, setHeaderDropDown] = useState('All Sales Return');
	const [sortDirection, setSortDirection] = useState({ id: 'asc' });
	const suburl = localStorage.getItem('suburl');
	const dispatch = useDispatch();
	const {
		page: advanceFilterPage,
		totalPages: advanceFilterTotalPages,
		pageSize: advanceFilterPageSize,
		orderBy: advanceFilterOrderby,
		sortBy: advanceFilterSortBy,
		selectedInputs,
		isAdvanceFilterApplied,
	} = useSelector((state) => state.advanceFilter);
	const [salesReturn, setSalesReturn] = useState({
		data: [],
		totalPages: 0,
		currentPage: 1,
	});
	// Restore list state (page, page_size, sorting) from sessionStorage if available
	const [salesReturnParams, setSalesReturnParams] = useState(() => {
		const savedState = sessionStorage.getItem('salesReturnParams');
		if (savedState) {
			sessionStorage.removeItem('salesReturnParams');
			return JSON.parse(savedState);
		}
		return {
			page: Number(searchParams.get('page')) || 1,
			page_size: Number(searchParams.get('page_size')) || 20,
			order_by: searchParams.get('order_by') || 'desc',
			sort_by: searchParams.get('sort_by') || 'created_at',
			filters: {},
		};
	});
	const [isLoading, setLoading] = useState(false);
	const [isLoader, setLoader] = useState(false);
	const navigate = useNavigate();
	const [isAdvanceFilterOpen, setIsAdvanceFilterOpen] = useState(false);
	const handleDeselectAll = (deselectAllRowsFn) => {
		setDeselectRows(() => deselectAllRowsFn);
	};
	const handlePageChange = (page) => {
		if (isAdvanceFilterApplied) {
			dispatch(setAdvanceFilterPage({ page }));
		} else {
			setSalesReturnParams((prev) => ({
				...prev,
				page,
			}));
		}
	};

	const onRowClick = (id) => {
		if (id && hasPermission('sales.sales_return.view', user?.role?.role_permissions)) {
			sessionStorage.setItem('salesReturnParams', JSON.stringify(salesReturnParams));
			navigate(`/user/${suburl}/sales-return-details/${id}`);
		}
	};

	const FilterDatalist = (dropDownData) => {
		if (isAdvanceFilterApplied && dropDownData === 'All Sales Return') {
			dispatch(clearAdvanceFilter());
			setHeaderDropDown('All Sales Return');
			return;
		}
		setHeaderDropDown(dropDownData);
		const updatedSelectedInput = selectedInputs.map((input) => {
			if (input.key === 'status') {
				return {
					...input,
					value: dropDownData,
				};
			}
			return input;
		});
		dispatch(setSelectedInput({ key: 'status', label: 'Status' }));
		dispatch(setSelectedInputValue({ key: 'status', value: dropDownData }));
		dispatch(setAdvanceFilter({ isAdvanceFilterApplied: true }));
		var selectInputData = [
			{ key: 'is_open', value: 'Open' },
			{ key: 'is_closed', value: 'Closed' },
			{ key: 'is_draft', value: 'Drafts' },
			{ key: 'is_pending_approval', value: 'Pending Approval' },
			{ key: 'is_approved', value: 'Approved' },
			{ key: 'is_declined', value: 'Declined' },
			{ key: 'is_receive_pending', value: 'Received Pending' },
			{ key: 'is_received', value: 'Received' },
			{ key: 'is_refunded', value: 'Refunded' },
		];
		dispatch(setSelectedInputData({ key: 'status', data: selectInputData }));
		const params = getFiltersParams(updatedSelectedInput);
		handleApplyFilters(params);
	};

	async function handleApplyFilters(params) {
		try {
			if (Object.keys(params).length === 0) {
				getSalesReturn();
				return;
			}
			if (params.status) {
				setHeaderDropDown(params.status);
			}
			const updatedParams = { ...params };
			if (updatedParams.status === 'Approved') {
				updatedParams.status = 'is_approved';
			}
			if (updatedParams.status === 'Drafts') {
				updatedParams.status = 'is_draft';
			}
			if (updatedParams.status === 'Pending Approval') {
				updatedParams.status = 'is_pending_approval';
			}
			if (updatedParams.status === 'Declined') {
				updatedParams.status = 'is_declined';
			}
			if (updatedParams.status === 'Received Pending') {
				updatedParams.status = 'is_receive_pending';
			}
			if (updatedParams.status === 'Received') {
				updatedParams.status = 'is_received';
			}
			if (updatedParams.status === 'Refunded') {
				updatedParams.status = 'is_refunded';
			}
			if (updatedParams.status === 'Open') {
				updatedParams.status = 'is_open';
			}
			if (updatedParams.status === 'Closed') {
				updatedParams.status = 'is_closed';
			}
			var { sales_return, total_pages } = await getSalesReturnList({
				order_by: advanceFilterOrderby,
				sort_by: advanceFilterSortBy,
				page: advanceFilterPage,
				page_size: advanceFilterPageSize,
				filters: updatedParams,
			});
			const returnsLists = mappedTableData(sales_return);
			setBulkAction([]);
			// Set active page for filtered sales return list
			setSalesReturn((prev) => ({
				...prev,
				data: returnsLists,
				totalPages: total_pages,
				currentPage: advanceFilterPage || 1,
			}));
			dispatch(setAdvanceFilterTotalPage({ totalPages: total_pages }));
			setIsAdvanceFilterOpen(false);
		} catch (error) {
			toast.error(error?.message || 'Something went wrong! Try again.');
		}
	}

	const sortDataList = (param) => {
		const newDirection = sortDirection[param.sort_by] === 'desc' ? 'asc' : 'desc';
		setSortDirection({ [param.sort_by]: newDirection });
		if (isAdvanceFilterApplied) {
			dispatch(setAdvanceFilter({ order_by: newDirection, sort_by: param.sort_by }));
			const params = getFiltersParams(selectedInputs);
			handleApplyFilters(params);
		} else {
			setSalesReturnParams((prev) => ({
				...prev,
				order_by: newDirection,
				sort_by: param.sort_by,
			}));
		}
	};

	async function handleOnFocusSelectedFilterInput(key) {
		let selectInputData = [];
		if (key !== 'status') {
			const { sales_return } = await getSalesReturnList({ ...salesReturnParams, page_size: 1000 });
			if (key === 'customer') {
				selectInputData = sales_return
					?.filter((item) => item['customer'])
					?.map((returns) => ({
						key: returns.customer.name,
						value: returns.customer.name,
					}));
			} else if (key === 'payment_mode') {
				selectInputData = sales_return
					?.filter((item) => item['payment_mode'] !== null)
					?.map((returns) => ({
						key: returns.payment_mode?.payment_method_name,
						value: returns.payment_mode?.payment_method_name,
					}));
			} else {
				selectInputData = sales_return
					?.filter((item) => item[key])
					?.map((returns) => ({
						key: returns[key],
						value: returns[key],
					}));
			}
		} else {
			if (key === 'status') {
				selectInputData = [
					{ key: 'is_open', value: 'Open' },
					{ key: 'is_closed', value: 'Closed' },
					{ key: 'is_draft', value: 'Drafts' },
					{ key: 'is_pending_approval', value: 'Pending Approval' },
					{ key: 'is_approved', value: 'Approved' },
					{ key: 'is_declined', value: 'Declined' },
					{ key: 'is_receive_pending', value: 'Received Pending' },
					{ key: 'is_received', value: 'Received' },
					{ key: 'is_refunded', value: 'Refunded' },
				];
			}

			if (key === 'received_status') {
				selectInputData = [
					{ key: 'Received', value: 'Received' },
					{ key: 'Pending', value: 'Refunded' },
				];
			}
		}
		const uniqueData = getUniqueFilterOptions(selectInputData);
		dispatch(setSelectedInputData({ key, data: uniqueData }));
		return uniqueData;
	}

	async function searchSelectedInputItems(key, value) {
		try {
			let optionsList;
			if (key === 'status') {
				optionsList = [
					{ key: 'is_open', value: 'Open' },
					{ key: 'is_closed', value: 'Closed' },
					{ key: 'is_draft', value: 'Drafts' },
					{ key: 'is_pending_approval', value: 'Pending Approval' },
					{ key: 'is_approved', value: 'Approved' },
					{ key: 'is_declined', value: 'Declined' },
					{ key: 'is_receive_pending', value: 'Received Pending' },
					{ key: 'is_received', value: 'Received' },
					{ key: 'is_refunded', value: 'Refunded' },
				];
			}
			if (key === 'received_status') {
				optionsList = [
					{ key: 'Received', value: 'Received' },
					{ key: 'Pending', value: 'Refunded' },
				];
			} else {
				const { sales_return } = await getSalesReturnList({
					page_size: 1000,
					filters: {
						key: value,
					},
				});
				optionsList = sales_return?.map((sr) => ({
					key: sr[key],
					value: sr[key],
				}));
			}

			const uniqueOptions = getUniqueFilterOptions(optionsList);
			dispatch(setSelectedInputData({ key, data: uniqueOptions }));
			return uniqueOptions;
		} catch (error) {}
	}

	function handleOpenBulkReturnModal() {
		const selectedList = salesReturn.data?.reduce((acc, item) => {
			if (bulkAction.includes(item.id)) {
				acc.push(item['return_number']);
				return acc;
			}
			return acc;
		}, []);
		setDeleteBulkReturnList(selectedList);
		setIsBulkDeleteModalOpen(true);
	}

	async function handleBulkDeleteSalesReturn() {
		try {
			var body = JSON.stringify({
				ids: bulkAction,
			});
			await deleteSalesReturn(body);
			toast.success('Sales Return delete successfully');
			setIsBulkDeleteModalOpen(false);
			getSalesReturn();
		} catch (error) {
			toast.error(error.message || 'Something went wrong!');
		}
	}

	const getSalesReturn = async () => {
		setLoading(true);
		try {
			var { sales_return, total_pages, page } = await getSalesReturnList(salesReturnParams);
			const salesReturnList = mappedTableData(sales_return);
			if (salesReturnList) {
				setBulkAction([]);
				setSalesReturn((prev) => ({
					...prev,
					data: salesReturnList,
					totalPages: total_pages,
					currentPage: page,
				}));
			} else {
				setSalesReturn((prev) => ({
					...prev,
					data: [],
					totalPages: total_pages,
					currentPage: page,
				}));
			}
		} catch (error) {
		} finally {
			setLoading(false);
		}
	};

	function mappedTableData(data) {
		return data?.map((ele) => {
			return {
				id: ele?.id,
				DATE: getDate(ele?.date),
				return_number: ele?.return_number,
				'CUSTOMER NAME': ele.customer?.name || 'N/A',
				STATUS: ele?.status || 'N/A',
				'RECEIVED STATUS': ele?.received_status || 'N/A',
				'REFUND MODE': ele?.payment_mode?.payment_method_name || 'N/A',
				AMOUNT: ele?.bill_total || 'N/A',
			};
		});
	}

	// async function handlePrintAndDownloadPdf(isPrint) {
	// 	setLoader(true);
	// 	const mergedDocDefinition = await generateSalesReturnPdf(bulkAction);
	// 	if (isPrint) {
	// 		pdfMake.createPdf(mergedDocDefinition).print();
	// 	} else {
	// 		pdfMake.createPdf(mergedDocDefinition).download();
	// 	}
	// 	setLoader(false);
	// }

	async function handlePrintAndDownloadPdf(
		ids = bulkAction.join(','),
		type = 'print',
		merge = false,
	) {
		try {
			setLoader(true);
			const pdf_details = await generateSalesReturnsPdf(
				ids === '' ? bulkAction.join(',') : ids,
				merge,
			);
			const url = pdf_details.signed_url;

			if (type === 'print') {
				window.open(url, '_blank');
				return;
			}

			if (type === 'download') {
				const fileName = `${merge ? 'merged' : pdf_details?.meta_data.return_number}_sales_return_${Date.now()}.pdf`;
				const response = await fetch(url);
				const blob = await response.blob();
				const blobUrl = window.URL.createObjectURL(blob);
				const link = document.createElement('a');
				link.href = blobUrl;
				link.download = fileName;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				window.URL.revokeObjectURL(blobUrl);
			}
		} catch (err) {
		} finally {
			setLoader(false);
		}
	}

	const setHeaderDropdownStatus = (filters) => {
		const statusMap = {
			is_open: 'Open',
			is_closed: 'Closed',
			is_draft: 'Drafts',
			is_pending_approval: 'Pending Approval',
			is_approved: 'Approved',
			is_declined: 'Declined',
			is_receive_pending: 'Received Pending',
			is_received: 'Received',
			is_refunded: 'Refunded',
		};
		if (filters.status !== undefined) {
			setHeaderDropDown(statusMap[filters.status]);
		}
	};

	const setFilterFromUrl = () => {
		const filters = {};
		searchParams.forEach((value, key) => {
			if (key !== 'page' && key !== 'page_size' && key !== 'order_by' && key !== 'sort_by') {
				filters[key] = value;
			}
		});
		setSortDirection({
			[searchParams.get('sort_by')]: searchParams.get('order_by') || '',
		});
		setHeaderDropdownStatus(filters);
		Object.entries(filters).forEach(([key, value]) => {
			const statusValueMap = {
				is_open: 'Open',
				is_closed: 'Closed',
				is_draft: 'Drafts',
				is_pending_approval: 'Pending Approval',
				is_approved: 'Approved',
				is_declined: 'Declined',
				is_receive_pending: 'Received Pending',
				is_received: 'Received',
				is_refunded: 'Refunded',
			};
			const fieldValue = key === 'status' ? statusValueMap[value] : value;
			dispatch(
				setSelectedInput({
					key: key,
					label: formatFilterName(key),
				}),
			);
			dispatch(
				setSelectedInputValue({
					key: key,
					value: fieldValue,
				}),
			);
			handleOnFocusSelectedFilterInput(key);
		});
		if (Object.keys(filters).length > 0) {
			dispatch(
				setAdvanceFilter({
					isAdvanceFilterApplied: true,
					page: Number(searchParams.get('page')) || salesReturnParams.page || 1,
					pageSize: Number(searchParams.get('page_size')) || salesReturnParams.page_size || 20,
					order_by: searchParams.get('order_by') || salesReturnParams.order_by || 'created_at',
					sort_by: searchParams.get('sort_by') || salesReturnParams.sort_by || 'desc',
				}),
			);
		}
	};

	useEffect(() => {
		setFilterFromUrl();
	}, []);

	function getParams(params) {
		const status = params.status;
		if (!status) return params;
		setHeaderDropDown(status);
		const statusMap = {
			Open: 'is_open',
			Closed: 'is_closed',
			Drafts: 'is_draft',
			'Pending Approval': 'is_pending_approval',
			Approved: 'is_approved',
			Declined: 'is_declined',
			'Received Pending': 'is_receive_pending',
			Received: 'is_received',
			Refunded: 'is_refunded',
		};
		return {
			...params,
			status: statusMap[status],
		};
	}

	const setUrlSearchParams = () => {
		const url = new URLSearchParams();
		// Normal params
		url.set('page', isAdvanceFilterApplied ? advanceFilterPage : salesReturnParams.page);
		url.set(
			'page_size',
			isAdvanceFilterApplied ? advanceFilterPageSize : salesReturnParams.page_size,
		);
		url.set('order_by', isAdvanceFilterApplied ? advanceFilterOrderby : salesReturnParams.order_by);
		url.set('sort_by', isAdvanceFilterApplied ? advanceFilterSortBy : salesReturnParams.sort_by);
		if (selectedInputs.length > 0) {
			const filters = getParams(getFiltersParams(selectedInputs));
			Object.entries(filters).forEach(([key, value]) => {
				if (value !== undefined && value !== null && value !== '') {
					url.set(key, value);
				}
			});
		} else {
			Object.entries(salesReturnParams.filters).forEach(([key, value]) => {
				url.set(key, value);
			});
		}
		setSearchParams(url);
	};

	useEffect(() => {
		setUrlSearchParams();
	}, [
		salesReturnParams,
		selectedInputs,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterOrderby,
		advanceFilterSortBy,
		isAdvanceFilterApplied,
	]);

	// Fetch sales return list when params, filter, or pagination changes
	useEffect(() => {
		const stockFilterKeys = ['customer_name', 'sales_return_number', 'payment_mode', 'status'];
		const hasStockFilter = stockFilterKeys.some((key) => searchParams.has(key));
		if (isAdvanceFilterApplied) {
			const params = getFiltersParams(selectedInputs);
			handleApplyFilters(params);
		} else {
			if (!hasStockFilter) {
				getSalesReturn();
			}
		}
	}, [
		salesReturnParams,
		isAdvanceFilterApplied,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterOrderby,
		advanceFilterSortBy,
	]);

	return {
		user,
		salesReturn,
		bulkAction,
		isLoading,
		suburl,
		navigate,
		setBulkAction,
		deselectRows,
		handlePrintAndDownloadPdf,
		headerDropDown,
		FilterDatalist,
		isAdvanceFilterApplied,
		dispatch,
		sortDirection,
		sortDataList,
		setHeaderDropDown,
		setIsAdvanceFilterOpen,
		handleApplyFilters,
		selectedInputs,
		handleOnFocusSelectedFilterInput,
		isBulkDeleteModelOpen,
		setIsBulkDeleteModalOpen,
		deleteBulkReturnList,
		isAdvanceFilterOpen,
		salesReturnParams,
		handleDeselectAll,
		onRowClick,
		searchSelectedInputItems,
		handlePageChange,
		advanceFilterPage,
		advanceFilterPageSize,
		advanceFilterTotalPages,
		handleOpenBulkReturnModal,
		isLoader,
		handleBulkDeleteSalesReturn,
		setSalesReturnParams,
	};
};
export default UseSalesReturn;
